import { useMemo, useReducer, useEffect } from 'react';
import { useTable, useSortBy, useExpanded } from 'react-table';
import { FaArrowDown, FaArrowUp } from 'react-icons/fa';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

import ThemeLink from '../../components/ThemeLink';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorText from '../../components/ErrorText';
import Button from '../../components/inputs/Button';
import ActionsDropdown from '../../components/ActionsDropdown';
import {
  formatDateCellShort,
  formatNumberWithCommas,
} from '../../helpers/reactTable';
import { AUDIENCES_QUERY } from '../../queries';
import ActionModal from '../../components/ActionModal';
import ToastNotification from '../../components/ToastNotification';
import {
  CLEAR_SUCCESS,
  initialState,
  reducer,
  START_ACTION,
} from '../../reducers/actionReducer';
import DeleteAudience from '../../components/actions/DeleteAudience';
import { calculateProductPrice } from '../../helpers/pricing';
import { useAuth } from '../../hooks/useAuth';
import AsyncFiltersDisplay from '../../components/AsyncFiltersDisplay';
import { currency } from '../../utils';

function SavedAudiencesTable({ data, dispatchAction }) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
      },
      {
        Header: 'Date Saved',
        accessor: 'updatedAt',
        Cell: formatDateCellShort,
      },
      {
        Header: 'Order Type',
        id: 'orderType',
        Cell: () => 'Audience',
      },
      {
        Header: 'Actions',
        id: 'actions',
        Cell: ({ row }) => (
          <ActionsDropdown
            row={row}
            audienceName={row.original.name}
            showEdit={true}
            showDelete={true}
            showBuy={true}
            showDownload={false}
            showActivate={false}
            showDuplicate={false}
            onEdit={() =>
              navigate('../../audiences/audiencebuilder', {
                state: {
                  initialLoadState: {
                    audienceId: row.original.id,
                    audienceName: row.original.name,
                  },
                },
              })
            }
            onDelete={() =>
              dispatchAction({
                type: START_ACTION,
                payload: {
                  name: 'Delete Audience',
                  component: (
                    <DeleteAudience
                      dispatchAction={dispatchAction}
                      audienceName={row.original.name}
                    />
                  ),
                },
              })
            }
            onBuy={() =>
              navigate('../../audiences/order/upsell', {
                state: {
                  orderDetails: {
                    coreProductName: 'audience',
                    audienceName: row.original.name,
                    recordsIncluded: row.original.recordsIncluded,
                  },
                },
              })
            }
          />
        ),
      },
      {
        Header: 'Price',
        id: 'price',
        Cell: ({ row }) => {
          const recordsIncluded = row.original.recordsIncluded;
          const price = calculateProductPrice(
            'audience',
            recordsIncluded,
            user.plan
          );
          return currency(price);
        },
      },
      {
        Header: 'Records Available',
        accessor: 'recordsAvailable',
        Cell: formatNumberWithCommas,
      },
      {
        Header: 'Records Included',
        accessor: 'recordsIncluded',
        Cell: formatNumberWithCommas,
      },
      {
        Header: 'Details',
        id: 'details',
        Cell: ({ row }) => (
          <span
            className="font-bold text-blue hover:text-primary"
            {...row.getToggleRowExpandedProps()}
          >
            {row.isExpanded ? 'Hide' : 'Show'}
          </span>
        ),
      },
    ],
    [dispatchAction, navigate, user.plan]
  );

  const initialSortBy = useMemo(() => [{ id: 'updatedAt', desc: true }], []);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: initialSortBy,
      },
    },
    useSortBy,
    useExpanded
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    visibleColumns,
  } = tableInstance;

  return (
    <div className="h-full overflow-auto" style={{ paddingBottom: rows.length > 0 ? '120px' : '24px' }}>
      <div className="overflow-visible">
        <table {...getTableProps()} className="w-full">
        <thead className="whitespace-nowrap">
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className="border-b-2 border-black p-2 hover:bg-gray-100"
                >
                  <div className="flex items-center">
                    {column.render('Header')}
                    {column.isSorted && (
                      <span className="pl-2">
                        {column.isSortedDesc ? <FaArrowDown /> : <FaArrowUp />}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()} className="whitespace-nowrap">
          {rows.map((row) => {
            prepareRow(row);
            return (
              <>
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <td className="p-2" {...cell.getCellProps()}>
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
                {row.isExpanded && (
                  <tr>
                    <td colSpan={visibleColumns.length}>
                      <div className="pt-2 px-14 pb-6 border-t w-full">
                        <AsyncFiltersDisplay audienceName={row.original.name} />
                      </div>
                    </td>
                  </tr>
                )}
              </>
            );
          })}
        </tbody>
      </table>
      {rows.length === 0 && (
        <p className="text-center pt-6">
          You don't have any saved audiences yet. You can build and buy
          audiences in the{' '}
          <ThemeLink to="../../audiences/audiencebuilder">
            audience builder.
          </ThemeLink>
        </p>
      )}
      </div>
    </div>
  );
}

export default function SavedAudiences() {
  const { loading, error, data } = useQuery(AUDIENCES_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  const [actionState, dispatchAction] = useReducer(reducer, initialState);
  const { success } = actionState;

  useEffect(() => {
    if (success) {
      setTimeout(
        () =>
          dispatchAction({
            type: CLEAR_SUCCESS,
          }),
        5000
      );
    }
  }, [success]);

  return (
    <div className="w-full">
      {loading && (
        <LoadingSpinner message="Getting your audiences..." className="mt-10" />
      )}
      {!loading && error && <ErrorText message={error.message} />}
      {!loading && !error && data && (
        <SavedAudiencesTable
          data={data.audiences}
          dispatchAction={dispatchAction}
        />
      )}
      {actionState.inProgress && (
        <ActionModal
          actionState={actionState}
          dispatchAction={dispatchAction}
          show={actionState.inProgress}
        />
      )}
      <ToastNotification
        message={actionState.success}
        success={true}
        show={actionState.success}
      />
    </div>
  );
}
